/**
 * Gold slot-machine announcement (Phase-3 scaffold). The gold metallic fill is
 * PURE CSS (gradient + background-clip:text + layered text-shadow + animated
 * background-position shimmer); GSAP drives ONLY the small→big pop (scale/opacity)
 * — never the gradient. Pops the latest announcement from the store, then fades.
 *
 * Phase 3 will drive these from the resolution flow ("You host tomorrow!",
 * "Tie! Rolling dice…", winner/loser callouts). The wiring point is the store's
 * `announcements` queue.
 */

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useGameStore } from '../state/store';
import { playSfx } from '../audio/sfx';

const DISMISS_MS = 3200;

export function Announcement() {
  const announcements = useGameStore((s) => s.announcements);
  const current = announcements[announcements.length - 1];

  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (current && ref.current) {
        gsap.fromTo(
          ref.current,
          { scale: 0.2, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.6, ease: 'elastic.out(1, 0.5)' },
        );
      }
    },
    { dependencies: [current?.id] },
  );

  useEffect(() => {
    if (!current) return;
    if (current.tone === 'win') playSfx('winJingle');
    const id = current.id;
    const t = setTimeout(() => {
      useGameStore.getState().dismissAnnouncement(id);
    }, DISMISS_MS);
    return () => clearTimeout(t);
  }, [current]);

  if (!current) return null;

  return (
    <div className="announcement">
      <div ref={ref} className={`announcement__text title-gold announcement--${current.tone}`}>
        {current.text}
      </div>
    </div>
  );
}
